import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {CodonTranslationTable} from 'biojs/BioConstants';

// The below types correspond to client request shapes in
// benchling/antibodies/nested_data_creation/network/rest_shared/request_types.py
// and related domain types in benchling/antibodies/nested_data_creation/core/types.py.
// Keep these in sync.

export interface TranslationParameters {
  geneticCode: CodonTranslationTable;
}

export interface DistinctDomainKeyDict {
  label: string;
}

export interface DistinctChainKeyDict {
  label: string;
}

export interface DistinctVariablePairKeyDict {
  vhVaVg: DistinctDomainKeyDict;
  vlVbVd: DistinctDomainKeyDict;
}

export const INITIAL_ANTIBODY_NDC_TRANSLATION_PARAMETERS: TranslationParameters = {
  geneticCode: CodonTranslationTable.STANDARD,
};

// ------------------------------------------------------------
// Modal Types and Constants
// ------------------------------------------------------------

export enum AntibodySpreadsheetImportStepKey {
  SELECT_FORMAT = 'SELECT_FORMAT',
  UPLOAD_SPREADSHEET = 'UPLOAD_SPREADSHEET',
  COLUMN_MAPPING = 'COLUMN_MAPPING',
  FINALIZE = 'FINALIZE',
  /** Only shown if there are validation errors upon submission */
  VALIDATION_ERRORS = 'VALIDATION_ERRORS',
}

export const ANTIBODY_SPREADSHEET_IMPORT_STEPS = [
  {key: AntibodySpreadsheetImportStepKey.SELECT_FORMAT, title: 'Select format & inputs'},
  {key: AntibodySpreadsheetImportStepKey.UPLOAD_SPREADSHEET, title: 'Upload spreadsheet'},
  {key: AntibodySpreadsheetImportStepKey.COLUMN_MAPPING, title: 'Map columns'},
  {key: AntibodySpreadsheetImportStepKey.FINALIZE, title: 'Finalize & create'},
  {key: AntibodySpreadsheetImportStepKey.VALIDATION_ERRORS, title: 'Validation errors'},
];

export interface SelectFormatPane {
  paneKey: AntibodySpreadsheetImportStepKey.SELECT_FORMAT;
}

export interface UploadSpreadsheetPane {
  paneKey: AntibodySpreadsheetImportStepKey.UPLOAD_SPREADSHEET;
}

export interface ColumnMappingPane {
  paneKey: AntibodySpreadsheetImportStepKey.COLUMN_MAPPING;
}

export interface FinalizePane {
  paneKey: AntibodySpreadsheetImportStepKey.FINALIZE;
}

export interface ValidationErrorsPane {
  paneKey: AntibodySpreadsheetImportStepKey.VALIDATION_ERRORS;
}

export type Pane =
  | SelectFormatPane
  | UploadSpreadsheetPane
  | ColumnMappingPane
  | FinalizePane
  | ValidationErrorsPane;

export const NDC_EXISTING_ENTITY_COMPONENT_TYPE_LABEL = 'Entity name / Registry ID';
export const NDC_DNA_BASES_COMPONENT_TYPE_LABEL = 'DNA bases';
export const NDC_AA_SEQUENCE_COMPONENT_TYPE_LABEL = 'Amino acids';

// ------------------------------------------------------------
// NDC Spreadsheet Import Types
// ------------------------------------------------------------

export enum NdcSpreadsheetComponentFormatType {
  PAIRS = 'PAIRS',
  DOMAINS = 'DOMAINS',
  CHAINS = 'CHAINS',
}

export enum NdcComponentSequenceType {
  DNA = 'DNA',
  AA = 'AA',
}

export enum NdcComponentInputType {
  NEW_SEQUENCE = 'NEW_SEQUENCE',
  EXISTING_ENTITY_NAME_OR_REGISTRY_ID = 'EXISTING_ENTITY_NAME_OR_REGISTRY_ID',
}

export interface TaggedDomainKeyDict extends DistinctDomainKeyDict {
  keyType: 'DOMAIN';
}

export interface TaggedChainKeyDict extends DistinctChainKeyDict {
  keyType: 'CHAIN';
}

export interface TaggedVariablePairKeyDict extends DistinctVariablePairKeyDict {
  keyType: 'VARIABLE_PAIR';
}

export interface NdcComponentType {
  componentSequenceType: NdcComponentSequenceType;
  componentInputType: NdcComponentInputType;
}

/** Represents a single spreadsheet column.
 *
 * Note that NdcSpreadsheetInterpretation is set iff column inference does not produce any errors. As a
 * result, NdcColumnState will only ever represent valid columns with non-empty inferredTypes. */
export interface NdcColumnState {
  columnName: string;
  /** The sample value for the column */
  sampleValue: string;
  /** The component format key for the column */
  componentFormatKey: TaggedDomainKeyDict | TaggedChainKeyDict | TaggedVariablePairKeyDict;
  /** The inferred types for the column. Always non-empty. */
  inferredTypes: Array<NdcComponentType>;
  /** The selected type for the column.
   *
   * If there is only one inferred type, selectedType is automatically set to that inferred type.
   * If there are multiple inferred types, selectedType is null until the user selects a type.
   *
   * The selectedType is used by the ColumnMappingPane to infer if it should display a warning to the user
   * that they should confirm the column type. It does not need to be set for the spreadsheet import to be
   * submitted, as in that case the 'highest priority' inferred type is used, i.e. the one that is displayed
   * to the user in the dropdown with the warning icon.
   */
  selectedType: NdcComponentType | null;
}

/** Interpretation of the spreadsheet.
 *
 * Note that NdcSpreadsheetInterpretation is set iff column inference does not produce any errors, and all
 * columns are successfully inferred. If any column produces an error, an NdcSpreadsheetInterpretation is not
 * created and set. */
export interface NdcSpreadsheetInterpretation {
  /** The columns of the spreadsheet and their inferred/selected types. The columns are in the order they
   * appear in the spreadsheet. */
  columns: Array<NdcColumnState>;
  /** Number of data rows, excluding header */
  rowCount: number;
}

export interface NdcGermlineConfig {
  /** The selected germline ID when using a single germline for all domains */
  selectedGermlineId: string | null;
  /** The selected germline ID for variable domains (only used when useMultipleGermlines is true) */
  selectedVariableGermlineId: string | null;
  /** The selected germline ID for constant domains (only used when useMultipleGermlines is true) */
  selectedConstantGermlineId: string | null;
  /** Whether the user has opted to specify separate germlines for variable and constant domains */
  useMultipleGermlines: boolean;
}

export interface NdcRelatedSchemasConfig {
  dnaDomainSchemaId: string | null;
  dnaChainSchemaId: string | null;
  dnaPairSchemaId: string | null;
  aaPairSchemaId: string | null;
}

export interface AntibodyComponentSchemaInfo {
  id: string;
  name: string;
}

export interface AntibodyComponentAndRelatedSchemas {
  /** AA domain schemas inferred from the enforced data model for the selected antibody schema. Multiple
   * schemas are possible since each AA domain in an antibody can have a different schema. */
  aaDomainSchemas: Array<AntibodyComponentSchemaInfo>;
  /** AA chain schemas inferred from the enforced data model for the selected antibody schema. Multiple
   * schemas are possible since each AA chain in an antibody can have a different schema. */
  aaChainSchemas: Array<AntibodyComponentSchemaInfo>;
  // The following are the full set of schema ids for each component type the user can specify. Selected
  // values are set in relatedSchemasConfig
  dnaDomainSchemaIds: Set<string>;
  dnaChainSchemaIds: Set<string>;
  dnaPairSchemaIds: Set<string>;
  aaPairSchemaIds: Set<string>;
}

export interface AntibodyNestedDataCreationSpreadsheetImportState {
  /** The selected format, used for building germlineConfigs at submit time */
  selectedFormat: AntibodyFormat | null;
  /** The schema API ID for the antibodies to be created */
  selectedSchemaId: string | null;
  /** The schema name for the antibodies to be created */
  selectedSchemaName: string | null;
  /** The project folder API ID for the antibodies to be created */
  selectedProjectFolderId: string | null;
  /** The registry API ID */
  selectedRegistryId: string | null;
  /** The starting point for the spreadsheet import, determining which columns are required and what template
   * is available for the user to download */
  selectedStartingType: NdcSpreadsheetComponentFormatType;
  /** Whether germline selectors are shown (LD flag: MOLBIO_ENABLE_ANTIBODY_AWARENESS).
   * Resolved during the machine's `initializing` state via evaluateLDFlag. */
  molbioEnableAntibodyAwareness: boolean;
  /** Germline selection configuration */
  germlineConfig: NdcGermlineConfig;

  // Spreadsheet Upload and Column Mapping Pane
  /** The uploaded spreadsheet file info */
  spreadsheetFile: File | null;
  /** The URL of the uploaded spreadsheet */
  spreadsheetURL: string | null;
  /** Backend-inferred column interpretations, and user edits to mappings. Null until inference completes, and
   * remains null if inference produces any errors. Mutually exclusive with columnInferenceErrorMessages. */
  spreadsheetInterpretation: NdcSpreadsheetInterpretation | null;
  /** Column inference error messages, e.g. missing columns, unrecognized column names. Empty when inference
   * succeeds. Mutually exclusive with spreadsheetInterpretation. */
  columnInferenceErrorMessages: Array<string>;

  // Finalize Pane
  /** The normalized configuration describing how DNA components will be translated into AA components */
  translationParameters: TranslationParameters;
  /** Selected schemas for the component types which users select (DNA domain, DNA chain, DNA pair, AA pair) */
  relatedSchemasConfig: NdcRelatedSchemasConfig;
  /** Antibody component and related schemas inferred from the enforced data model for the selected antibody
   * schema, used to populate the FinalizePane dropdowns. Null while loading or if the fetch hasn't started yet. */
  componentAndRelatedSchemas: AntibodyComponentAndRelatedSchemas | null;
  /** Error message for component and related schemas query, e.g. antibody schema not found in registry, or
   * missing schema ids for user-selectable related schemas. */
  componentAndRelatedSchemasErrorMessage: string | null;
  /** The selected document ID for sending a receipt of antibody creation. Null means no receipt. */
  selectedDocumentId: string | null;

  // Validation Errors Pane
  /** Validation errors returned by the backend after submitting the spreadsheet */
  validationErrors: Array<NdcSpreadsheetImportValidationError>;
}

export const INITIAL_GERMLINE_CONFIG: NdcGermlineConfig = {
  selectedGermlineId: null,
  selectedVariableGermlineId: null,
  selectedConstantGermlineId: null,
  useMultipleGermlines: false,
};

export const INITIAL_RELATED_SCHEMAS_CONFIG: NdcRelatedSchemasConfig = {
  dnaDomainSchemaId: null,
  dnaChainSchemaId: null,
  dnaPairSchemaId: null,
  aaPairSchemaId: null,
};

export const INITIAL_ANTIBODY_NESTED_DATA_CREATION_SPREADSHEET_IMPORT_STATE: AntibodyNestedDataCreationSpreadsheetImportState =
  {
    selectedFormat: null,
    selectedSchemaId: null,
    selectedSchemaName: null,
    selectedProjectFolderId: null,
    selectedRegistryId: null,
    selectedStartingType: NdcSpreadsheetComponentFormatType.DOMAINS,
    molbioEnableAntibodyAwareness: false,
    germlineConfig: INITIAL_GERMLINE_CONFIG,
    spreadsheetFile: null,
    spreadsheetURL: null,
    spreadsheetInterpretation: null,
    columnInferenceErrorMessages: [],
    translationParameters: INITIAL_ANTIBODY_NDC_TRANSLATION_PARAMETERS,
    relatedSchemasConfig: INITIAL_RELATED_SCHEMAS_CONFIG,
    componentAndRelatedSchemas: null,
    componentAndRelatedSchemasErrorMessage: null,
    selectedDocumentId: null,
    validationErrors: [],
  };

// ------------------------------------------------------------
// API Request and Response Types
// ------------------------------------------------------------

// Inferring Columns

export interface NdcColumnInferenceRequest {
  spreadsheetLocation: string;
  proteinSchemaId: string;
  componentFormatType: NdcSpreadsheetComponentFormatType;
}

export interface NdcSpreadsheetInferredColumnResponse {
  responseType: 'INFERRED_COLUMN';
  columnName: string;
  sampleValue: string;
  componentFormatKey: TaggedDomainKeyDict | TaggedChainKeyDict | TaggedVariablePairKeyDict;
  inferredComponentTypes: Array<NdcComponentType>;
}

export interface NdcSpreadsheetInferredColumnErrorResponse {
  responseType: 'ERROR';
  columnName: string;
  sampleValue: string;
  errorMessage: string;
}

export interface NdcSpreadsheetMissingColumnErrorResponse {
  responseType: 'MISSING';
  errorMessage: string;
}

export function isNdcColumnInferenceResult(
  column:
    | NdcSpreadsheetInferredColumnResponse
    | NdcSpreadsheetInferredColumnErrorResponse
    | NdcSpreadsheetMissingColumnErrorResponse
): column is NdcSpreadsheetInferredColumnResponse {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'inferredComponentTypes' in column;
}

export function isNdcColumnInferenceError(
  column:
    | NdcSpreadsheetInferredColumnResponse
    | NdcSpreadsheetInferredColumnErrorResponse
    | NdcSpreadsheetMissingColumnErrorResponse
): column is NdcSpreadsheetInferredColumnErrorResponse {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'responseType' in column && column.responseType === 'ERROR';
}

export function isNdcColumnInferenceMissingError(
  column:
    | NdcSpreadsheetInferredColumnResponse
    | NdcSpreadsheetInferredColumnErrorResponse
    | NdcSpreadsheetMissingColumnErrorResponse
): column is NdcSpreadsheetMissingColumnErrorResponse {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'responseType' in column && column.responseType === 'MISSING';
}

export interface NdcColumnInferenceResponse {
  columnTypes: Array<
    | NdcSpreadsheetInferredColumnResponse
    | NdcSpreadsheetInferredColumnErrorResponse
    | NdcSpreadsheetMissingColumnErrorResponse
  >;
  rowCount: number;
}

export function isNdcColumnInferenceResponse(
  response: NdcColumnInferenceResponse | {errorMessage: string}
): response is NdcColumnInferenceResponse {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'columnTypes' in response;
}

// Importing Spreadsheet

/** A single domain-key-to-germline mapping sent in the API request */
export interface NdcGermlineConfigRequest {
  domainKey: DistinctDomainKeyDict;
  germlineId: string;
}

/* Flattened version of NdcColumnType that unifies selected and inferred column types */
export type NdcSpreadsheetImportColumnType = Pick<NdcColumnState, 'componentFormatKey' | 'columnName'> &
  NdcComponentType;

export type NdcSpreadsheetImportRequest = {
  folderId: string;
  spreadsheetLocation: string;
  columns: Array<NdcSpreadsheetImportColumnType>;
  translationParameters: TranslationParameters;
  documentId: string | null;
  proteinSchemaId: string;
  germlineConfigs: Array<NdcGermlineConfigRequest> | null;
} & NdcRelatedSchemasConfig;

/** Response from the backend when there are no validation errors */
export interface NdcSpreadsheetImportResponse {
  ndcWorkspaceUrl?: string;
}

export interface NdcSpreadsheetImportValidationError {
  rowIndex: number;
  errorMessage: string;
}

/** When there are validation errors, the backend will throw a BadRequestError of this shape */
export interface NdcSpreadsheetImportValidationErrorBadRequest {
  validationErrors: Array<NdcSpreadsheetImportValidationError>;
}
